import { useEffect, useState } from "react";
import type { ElementType } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import i18n from "@/lib/i18n";
import {
  AlertCircle,
  BarChart3,
  CheckCircle,
  Loader2,
  Map,
  Package,
  Truck,
  X,
} from "lucide-react";
import { consumeSessionExpired } from "@/lib/api";
import { AppLogo } from "@/components/ui/AppLogo";
import type { AxiosError } from "axios";
import { Button } from "@/components/ui/Button";
import { useAuthStore } from "@/store/auth";
import { MfaChallengeForm } from "@/features/mfa/components/MfaChallengeForm";
import { resolveSso } from "@/api/sso";
import { isSafeSsoUrl } from "@/lib/isSafeSsoUrl";
import { extractApiErrorMessage } from "@/lib/apiErrors";
import type { User } from "@/types/auth";

type LoginFormValues = {
  email: string;
  password: string;
};

/** Routes a freshly authenticated user to their role-specific landing page. */
function redirectForRole(
  role: string | undefined,
  navigate: (to: string, opts: { replace: boolean }) => void,
): void {
  if (role === "driver") {
    navigate("/driver-mobile", { replace: true });
  } else if (role === "superadmin") {
    navigate("/admin/dashboard", { replace: true });
  } else if (role === "dealer") {
    navigate("/dealer/dashboard", { replace: true });
  } else {
    navigate("/dashboard", { replace: true });
  }
}

// ---------------------------------------------------------------------------
// Decorative SVG — abstract route graphic (3 nodes connected by lines)
// ---------------------------------------------------------------------------
function RouteDecoration() {
  return (
    <svg
      viewBox="0 0 220 160"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full max-w-[280px] opacity-20"
      aria-hidden="true"
    >
      {/* Connection lines */}
      <line
        x1="40"
        y1="120"
        x2="110"
        y2="50"
        stroke="white"
        strokeWidth="2"
        strokeDasharray="6 4"
      />
      <line
        x1="110"
        y1="50"
        x2="180"
        y2="100"
        stroke="white"
        strokeWidth="2"
        strokeDasharray="6 4"
      />
      <line
        x1="180"
        y1="100"
        x2="130"
        y2="140"
        stroke="white"
        strokeWidth="2"
        strokeDasharray="6 4"
      />
      {/* Nodes */}
      <circle cx="40" cy="120" r="10" fill="white" fillOpacity="0.6" />
      <circle cx="40" cy="120" r="5" fill="white" />
      <circle cx="110" cy="50" r="14" fill="white" fillOpacity="0.6" />
      <circle cx="110" cy="50" r="7" fill="white" />
      <circle cx="180" cy="100" r="10" fill="white" fillOpacity="0.6" />
      <circle cx="180" cy="100" r="5" fill="white" />
      <circle cx="130" cy="140" r="8" fill="white" fillOpacity="0.6" />
      <circle cx="130" cy="140" r="4" fill="white" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// LoginPage
// ---------------------------------------------------------------------------
export default function LoginPage() {
  const { t, i18n: i18nInstance } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated } = useAuthStore();
  const user = useAuthStore((s) => s.user);
  const [apiError, setApiError] = useState<string | null>(null);
  // MFA challenge state — the interim mfa_token is held in local component
  // state only and is NEVER persisted as an auth token.
  const [challenge, setChallenge] = useState<{ mfaToken: string } | null>(null);
  const [showResetBanner, setShowResetBanner] = useState(
    searchParams.get("reset") === "success",
  );
  // TASK-4738: an involuntary session expiry redirects here via `forceLogout()`, which sets a
  // one-shot sessionStorage flag. Consumed in a lazy initializer so it is read exactly once on
  // mount (a plain call in the body would re-run on every render and clear the flag before the
  // first paint under StrictMode's double-invoke). A deliberate "Sign out" never sets the flag,
  // so this banner is specific to expiry.
  const [sessionExpired, setSessionExpired] = useState(consumeSessionExpired);
  // SSO affordance state — a toggle reveals an email field that resolves the
  // tenant/platform SSO login URL and starts the SP-initiated flow.
  const [showSso, setShowSso] = useState(false);
  const [ssoEmail, setSsoEmail] = useState("");
  const [ssoError, setSsoError] = useState<string | null>(null);
  const [ssoLoading, setSsoLoading] = useState(false);

  function toggleLang() {
    const next = i18nInstance.language === "ru" ? "en" : "ru";
    void i18n.changeLanguage(next);
    localStorage.setItem("lang", next);
  }

  // Zod schema is created inside the component so validation messages are
  // resolved through t() at render time, picking up the active locale.
  const loginSchema = z.object({
    email: z
      .string()
      .min(1, t("login.emailRequired"))
      .email(t("login.emailInvalid")),
    password: z.string().min(6, t("login.passwordMin")),
  });

  // Redirect already-authenticated users to the appropriate page.
  useEffect(() => {
    if (isAuthenticated && user?.role === "superadmin") {
      void navigate("/admin/dashboard", { replace: true });
    } else if (isAuthenticated && user?.role === "dealer") {
      void navigate("/dealer/dashboard", { replace: true });
    } else if (isAuthenticated) {
      void navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (values: LoginFormValues) => {
    setApiError(null);
    try {
      const result = await login(values.email, values.password);
      if (result.status === "mfa_required") {
        // Credentials valid but MFA is enabled — switch to the code-entry step.
        // No tokens were persisted; the interim token stays in local state.
        setChallenge({ mfaToken: result.mfaToken });
        return;
      }
      redirectForRole(result.user?.role, navigate);
    } catch (err) {
      // Enforced-SSO: the tenant requires federated login. The backend returns
      // a 403 with `sso_required: true` + `sso_login_url`. Route the browser to
      // SSO instead of showing a credential error (superadmin is exempt on the
      // backend and never hits this branch).
      const axiosErr = err as AxiosError<{
        detail?: string;
        sso_required?: boolean;
        sso_login_url?: string;
      }>;
      const body = axiosErr.response?.data;
      if (body?.sso_required && body.sso_login_url) {
        // Only navigate to a same-origin/relative URL — never trust the backend
        // value blindly, which would allow an open redirect off-site.
        if (isSafeSsoUrl(body.sso_login_url)) {
          window.location.assign(body.sso_login_url);
          return;
        }
        setApiError(t("login.invalidCredentials"));
        return;
      }
      // Otherwise resolve the failure through the shared maps (BUG-677): the
      // backend's `detail` is English («Invalid email or password.», «Too many
      // login attempts…», «This company account is deactivated…»), and printing
      // it verbatim showed English on the one screen every user sees first.
      setApiError(
        extractApiErrorMessage(err, t, t("login.invalidCredentials")),
      );
    }
  };

  // Resolves an email to its SSO login URL and starts the SP-initiated flow via
  // a full-page browser navigation. A 404 (no SSO for this email) shows inline.
  const onSsoSubmit = async () => {
    const email = ssoEmail.trim();
    setSsoError(null);
    if (!email) {
      setSsoError(t("login.sso.emailRequired"));
      return;
    }
    setSsoLoading(true);
    try {
      const { sso_login_url } = await resolveSso(email);
      // Full-page navigation (NOT XHR): the SSO login endpoint 302-redirects to
      // the IdP. `sso_login_url` is an app-origin server path (`/api/v1/...`).
      // Validate same-origin/relative first — a malicious value would otherwise
      // let the backend redirect the user off-site (open redirect).
      if (!isSafeSsoUrl(sso_login_url)) {
        setSsoError(t("login.sso.notFound"));
        setSsoLoading(false);
        return;
      }
      window.location.assign(sso_login_url);
    } catch {
      setSsoError(t("login.sso.notFound"));
      setSsoLoading(false);
    }
  };

  const handleMfaVerified = (verifiedUser: User) => {
    setChallenge(null);
    redirectForRole(verifiedUser.role, navigate);
  };

  const features: Array<{ icon: ElementType; key: string }> = [
    { icon: Map, key: "login.feature1" },
    { icon: Package, key: "login.feature2" },
    { icon: Truck, key: "login.feature3" },
    { icon: BarChart3, key: "login.feature4" },
  ];

  return (
    <div className="flex h-screen w-full">
      {/* ----------------------------------------------------------------- */}
      {/* Left panel — dark navy branding                                    */}
      {/* ----------------------------------------------------------------- */}
      <div className="relative hidden h-full flex-col items-center justify-center overflow-hidden bg-brand-sidebar px-12 lg:flex lg:w-1/2">
        {/* Subtle dot-grid background pattern */}
        <div className="pointer-events-none absolute inset-0 opacity-10 [background-image:radial-gradient(circle,white_1px,transparent_1px)] [background-size:28px_28px]" />

        <div className="relative z-10 flex w-full max-w-sm flex-col">
          {/* Logo */}
          <div className="mb-10">
            <AppLogo variant="dark" height={72} />
          </div>

          {/* Headline */}
          <h2 className="text-4xl font-bold leading-tight text-white">
            {t("login.headline")}
            <br />
            <span className="text-brand-primary">
              {t("login.headlineAccent")}
            </span>
          </h2>
          <p className="mt-4 text-base text-white/50">{t("login.tagline")}</p>

          {/* Feature bullets */}
          <ul className="mt-10 space-y-4">
            {features.map((item) => (
              <li key={item.key} className="flex items-center gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/10">
                  <item.icon
                    className="h-4 w-4 text-white/70"
                    aria-hidden="true"
                  />
                </span>
                <span className="text-sm font-medium text-white/70">
                  {t(item.key)}
                </span>
              </li>
            ))}
          </ul>

          {/* Decorative route graphic — pushed to bottom */}
          <div className="mt-12 opacity-30">
            <RouteDecoration />
          </div>
        </div>
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* Right panel — login form                                           */}
      {/* ----------------------------------------------------------------- */}
      <div className="flex flex-1 flex-col items-center justify-center bg-gray-50/50 px-6 py-12">
        <div className="w-full max-w-sm">
          {/* Mobile logo (shown only on small screens) */}
          <div className="mb-8 lg:hidden">
            <AppLogo height={36} />
          </div>

          {/* Session-expiry notice (TASK-4738). Amber, not red: the session simply ran
              out — nothing failed and the user did nothing wrong. */}
          {sessionExpired && (
            <div
              role="status"
              className="mb-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <p className="flex-1 text-sm text-amber-800">
                {t("login.sessionExpired")}
              </p>
              <button
                type="button"
                onClick={() => setSessionExpired(false)}
                className="text-amber-600 hover:text-amber-800"
                aria-label={t("common.dismiss")}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Password reset success banner */}
          {showResetBanner && (
            <div className="mb-4 flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
              <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
              <p className="flex-1 text-sm text-green-800">
                {t("login.passwordResetSuccess")}
              </p>
              <button
                type="button"
                onClick={() => setShowResetBanner(false)}
                className="text-green-600 hover:text-green-800"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          <div className="relative rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
            <button
              type="button"
              onClick={toggleLang}
              className="absolute right-4 top-4 rounded-md px-2 py-1 text-xs font-semibold text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            >
              {i18nInstance.language === "ru" ? "EN" : "RU"}
            </button>
            {challenge ? (
              <MfaChallengeForm
                mfaToken={challenge.mfaToken}
                onVerified={handleMfaVerified}
                onBack={() => setChallenge(null)}
              />
            ) : (
              <>
                <h1 className="mb-1 text-2xl font-bold text-gray-900">
                  {t("login.welcomeBack")}
                </h1>
                <p className="mb-7 text-sm text-gray-400">
                  {t("login.signInSubtitle")}
                </p>

                <form
                  onSubmit={(e) => void handleSubmit(onSubmit)(e)}
                  noValidate
                  className="flex flex-col gap-5"
                >
                  {/* Email field */}
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="email"
                      className="text-sm font-medium text-gray-700"
                    >
                      {t("login.emailLabel")}
                    </label>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      placeholder={t("login.emailPlaceholder")}
                      aria-invalid={errors.email ? "true" : "false"}
                      {...register("email")}
                      className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 transition-shadow focus:border-transparent focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    />
                    {errors.email && (
                      <p role="alert" className="text-sm text-red-500">
                        {errors.email.message}
                      </p>
                    )}
                  </div>

                  {/* Password field */}
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center justify-between">
                      <label
                        htmlFor="password"
                        className="text-sm font-medium text-gray-700"
                      >
                        {t("login.passwordLabel")}
                      </label>
                      <Link
                        to="/forgot-password"
                        className="text-sm text-brand-primary hover:underline"
                        tabIndex={-1}
                      >
                        {t("login.forgotPassword")}
                      </Link>
                    </div>
                    <input
                      id="password"
                      type="password"
                      autoComplete="current-password"
                      placeholder={t("login.passwordPlaceholder")}
                      aria-invalid={errors.password ? "true" : "false"}
                      {...register("password")}
                      className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 transition-shadow focus:border-transparent focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    />
                    {errors.password && (
                      <p role="alert" className="text-sm text-red-500">
                        {errors.password.message}
                      </p>
                    )}
                  </div>

                  {/* API / non-field error */}
                  {apiError && (
                    <p role="alert" className="text-sm text-red-500">
                      {apiError}
                    </p>
                  )}

                  {/* Submit */}
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isSubmitting}
                    className="w-full rounded-lg py-2.5"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t("login.signingIn")}
                      </>
                    ) : (
                      t("login.signIn")
                    )}
                  </Button>
                </form>

                {/* ----------------------------------------------------------- */}
                {/* SSO affordance — divider + "Sign in with SSO" toggle        */}
                {/* ----------------------------------------------------------- */}
                <div className="mt-6">
                  <div className="flex items-center gap-3">
                    <span className="h-px flex-1 bg-gray-200" />
                    <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
                      {t("login.sso.or")}
                    </span>
                    <span className="h-px flex-1 bg-gray-200" />
                  </div>

                  {!showSso ? (
                    <button
                      type="button"
                      onClick={() => {
                        setShowSso(true);
                        setSsoError(null);
                      }}
                      className="mt-4 w-full rounded-lg border border-gray-200 bg-white py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                    >
                      {t("login.sso.signInWithSso")}
                    </button>
                  ) : (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        void onSsoSubmit();
                      }}
                      noValidate
                      className="mt-4 flex flex-col gap-3"
                    >
                      <div className="flex flex-col gap-1.5">
                        <label
                          htmlFor="sso-email"
                          className="text-sm font-medium text-gray-700"
                        >
                          {t("login.sso.emailLabel")}
                        </label>
                        <input
                          id="sso-email"
                          type="email"
                          autoComplete="email"
                          placeholder={t("login.emailPlaceholder")}
                          value={ssoEmail}
                          onChange={(e) => setSsoEmail(e.target.value)}
                          aria-invalid={ssoError ? "true" : "false"}
                          className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 transition-shadow focus:border-transparent focus:outline-none focus:ring-2 focus:ring-brand-primary"
                        />
                        {ssoError && (
                          <p role="alert" className="text-sm text-red-500">
                            {ssoError}
                          </p>
                        )}
                      </div>
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={ssoLoading}
                        className="w-full rounded-lg py-2.5"
                      >
                        {ssoLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {t("login.sso.continuing")}
                          </>
                        ) : (
                          t("login.sso.continue")
                        )}
                      </Button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowSso(false);
                          setSsoError(null);
                        }}
                        className="text-sm text-gray-400 hover:text-gray-600"
                      >
                        {t("login.sso.usePassword")}
                      </button>
                    </form>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
